import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, History, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  addLocalDays,
  classifyPerformanceZone,
  toIsoDate,
  toPrecedingBusinessDay,
} from "@normax-performance/shared-format";
import type {
  CreateWorkLineInput,
  UpdateWorkLineInput,
  WorkLineDto,
} from "@normax-performance/shared-types";
import { useAuth } from "../../app/providers/AuthProvider";
import { useLocale } from "../../i18n/LocaleProvider";
import {
  calculationPolicyApi,
  catalogApi,
  companyPerformanceSettingsApi,
  dailyEntryApi,
  workersApi,
  workLinesApi,
} from "../../lib/api-client/endpoints";
import { ApiError } from "../../lib/api-client/client";
import { Badge, Button, Input, Modal, useToast } from "../../shared-ui/primitives";
import { EntryHeader } from "./components/EntryHeader";
import { EntryGrid } from "./components/EntryGrid";
import { MinutesSumBadge } from "./components/MinutesSumBadge";
import { DraftStatusBadge } from "./components/DraftStatusBadge";
import { DailyEntryHistoryModal } from "./components/DailyEntryHistoryModal";
import { DraftRecoveryBanner } from "./components/DraftRecoveryBanner";
import {
  isNeutralRow,
  isNormedRow,
  isUnnormedRow,
  type NeutralRowState,
  type NormedRowState,
  type RowState,
  type UnnormedRowState,
} from "./daily-entry.types";
import { clearDraftSnapshot, loadDraftSnapshot } from "./draft-storage";
import { useDraftAutosave } from "./hooks/useDraftAutosave";
import { useDayAggregatePreview } from "./hooks/useDayAggregatePreview";
import "./DailyEntryPage.css";

function lineToRow(line: WorkLineDto): RowState {
  const base = {
    localId: `server-${line.id}`,
    serverId: line.id,
    performedAtDepartmentId: line.performedAtDepartmentId,
    workOrderReference: line.workOrderReference,
    note: line.note,
    durationMinutes: line.durationMinutes,
    expanded: false,
    dirty: false,
  };
  if (line.entryType === "NEUTRAL_ACTIVITY") {
    return { ...base, entryType: "NEUTRAL_ACTIVITY", neutralActivityId: line.neutralActivityId } as NeutralRowState;
  }
  if (line.entryType === "UNNORMED_WORK") {
    return {
      ...base,
      entryType: "UNNORMED_WORK",
      operationId: line.operationId,
      productId: line.productId,
      reportedQuantity: line.reportedQuantity,
      unitId: line.unitId,
    } as UnnormedRowState;
  }
  return {
    ...base,
    entryType: "NORMED_WORK",
    productId: line.productId,
    operationId: line.operationId,
    reportedQuantity: line.reportedQuantity ?? "",
    unitId: line.unitId,
  } as NormedRowState;
}

function rowToCreateInput(row: RowState): CreateWorkLineInput {
  if (isNeutralRow(row)) {
    return {
      entryType: "NEUTRAL_ACTIVITY",
      neutralActivityId: row.neutralActivityId!,
      performedAtDepartmentId: row.performedAtDepartmentId,
      workOrderReference: row.workOrderReference,
      note: row.note,
      durationMinutes: row.durationMinutes,
    };
  }
  if (isUnnormedRow(row)) {
    return {
      entryType: "UNNORMED_WORK",
      operationId: row.operationId!,
      productId: row.productId,
      reportedQuantity: row.reportedQuantity,
      unitId: row.unitId,
      performedAtDepartmentId: row.performedAtDepartmentId,
      workOrderReference: row.workOrderReference,
      note: row.note,
      durationMinutes: row.durationMinutes,
    };
  }
  return {
    entryType: "NORMED_WORK",
    productId: row.productId,
    operationId: row.operationId!,
    reportedQuantity: row.reportedQuantity,
    unitId: row.unitId,
    performedAtDepartmentId: row.performedAtDepartmentId,
    workOrderReference: row.workOrderReference,
    note: row.note,
    durationMinutes: row.durationMinutes,
  };
}

/**
 * Maps a known, stable error `code` to non-technical, translated copy —
 * never pattern-matches on backend message text (API_AND_AUTH_CONTRACT_MAP.md
 * §4). "Calculation policy" is an internal technical term
 * (NORMAX_PERFORMANCE_DAILY_ENTRY_FINALIZATION_UX_FIX_REPORT.md §13) and must
 * never reach this UI verbatim. Every other error code falls through to the
 * backend's own message unchanged — already user-facing Serbian/localized
 * text for validation errors etc., not something this task re-implements.
 */
function mapDailyEntryErrorMessage(error: unknown, t: TFunction<"dailyEntry">, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.code === "CALCULATION_POLICY_MISSING") return t("errors.calculationPolicyMissing");
    if (error.code === "DAILY_ENTRY_MINUTES_MISMATCH") return t("errors.minutesMismatch");
    return error.message;
  }
  return fallback;
}

function isRowComplete(row: RowState): boolean {
  if (Number(row.durationMinutes) <= 0 || row.durationMinutes.trim() === "") return false;
  if (isNormedRow(row)) return !!row.operationId && row.reportedQuantity.trim() !== "";
  if (isNeutralRow(row)) return !!row.neutralActivityId;
  return !!row.operationId;
}

export function DailyEntryPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation("dailyEntry");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { intlLocale } = useLocale();
  const isDirector = user?.role === "DIRECTOR";
  // Kept as `readOnly` (unchanged name) for the page title / DIRECTOR-only
  // concerns below — DIRECTOR is read-only regardless of status.
  const readOnly = isDirector;

  // Defaults to YESTERDAY, not today: operators enter yesterday's production
  // results today (NORMAX_PERFORMANCE_OPERATION_CODE_DAILY_ENTRY_DEFAULT_DATE_FIX_REPORT.md).
  // `addLocalDays` shifts by local calendar fields (never UTC/millisecond
  // subtraction, which drifts across DST transitions). If that lands on a
  // Saturday or Sunday (no production happens then), `toPrecedingBusinessDay`
  // rolls it back further to the preceding Friday — e.g. opening the app on
  // a Monday defaults to Friday, not Sunday. This initializer — a lazy
  // `useState` function — runs exactly once at mount, so this is purely an
  // initial value: any explicitly-supplied date and any manual change via
  // `setWorkDate` below are never overridden by it afterward.
  const [workDate, setWorkDate] = useState(() =>
    toIsoDate(toPrecedingBusinessDay(addLocalDays(new Date(), -1))),
  );
  const [workerId, setWorkerId] = useState<string | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [entryId, setEntryId] = useState<string | null>(null);
  const [entryStatus, setEntryStatus] = useState<"draft" | "confirmed" | "locked">("draft");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [recoverableDraft, setRecoverableDraft] = useState<{ savedAt: string; rows: RowState[] } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingNav, setPendingNav] = useState<null | (() => void)>(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  // "Ne prolazi tiho" (2026-09-09 brainstorm) — deleting a persisted row
  // always requires a reason, prompted right at the moment of deletion
  // (not deferred to a later save). `deleteRowPrompt` holds the row
  // pending confirmation; `null` means no prompt is open.
  const [deleteRowPrompt, setDeleteRowPrompt] = useState<RowState | null>(null);
  const [deleteRowReason, setDeleteRowReason] = useState("");

  // "Obriši ceo dan" — same reason-required pattern, one level up (the
  // whole daily_work_entry, not a single row).
  const [deleteEntryModalOpen, setDeleteEntryModalOpen] = useState(false);
  const [deleteEntryReason, setDeleteEntryReason] = useState("");

  // Shown INSIDE whichever delete modal is open (audit finding #2) — a
  // failure must not silently land in `saveError`'s page-level banner,
  // which the modal's own overlay visually hides from the user.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const workerFieldRef = useRef<HTMLInputElement | null>(null);

  // Bare no-arg calls (Operational MVP Slice A note: identical to the
  // pre-Slice-A behavior — `includeInactive` omitted -> active-only, exactly
  // as before; wrapped in arrow functions only because react-query's typed
  // QueryFunctionContext no longer structurally matches an optional-params
  // signature, not because the request itself changed).
  const workersQuery = useQuery({ queryKey: ["workers"], queryFn: () => workersApi.list() });
  const productsQuery = useQuery({ queryKey: ["products"], queryFn: () => catalogApi.products() });
  const operationsQuery = useQuery({ queryKey: ["operations"], queryFn: () => catalogApi.operations() });
  const neutralActivitiesQuery = useQuery({
    queryKey: ["neutral-activities"],
    queryFn: () => catalogApi.neutralActivities(),
  });
  const unitsQuery = useQuery({ queryKey: ["units"], queryFn: catalogApi.units });
  const validCombinationsQuery = useQuery({
    queryKey: ["valid-combinations", workDate],
    queryFn: () => catalogApi.validCombinations({ date: workDate }),
    enabled: !!workDate,
  });

  const entryQuery = useQuery({
    queryKey: ["daily-entry", workerId, workDate],
    queryFn: () => dailyEntryApi.find(workerId!, workDate),
    enabled: !!workerId && !!workDate,
  });

  // Contextual note for a previously-deleted day (2026-09-09 brainstorm,
  // "Obriši ceo dan") — only meaningful when there is currently NO entry
  // for this exact worker/date (`entryQuery` resolved successfully to
  // `null`, not merely still loading). A normal, expected `null` result
  // for the vast majority of worker/date combinations — not an error.
  const deletedInfoQuery = useQuery({
    queryKey: ["daily-entry-deleted-info", workerId, workDate],
    queryFn: () => dailyEntryApi.deletedInfo(workerId!, workDate),
    enabled: !!workerId && !!workDate && entryQuery.isSuccess && entryQuery.data === null,
  });

  // The active company performance/work-time settings for THIS work_date
  // (contract §E — never "today's" settings for a possibly-past date).
  // Authoritative source for the expected-minutes fund and zone thresholds
  // shown on this page; no local/demo fallback constant is used anymore.
  const performanceSettingsQuery = useQuery({
    queryKey: ["company-performance-settings", workDate],
    queryFn: () => companyPerformanceSettingsApi.current(workDate),
    enabled: !!workDate,
  });
  const performanceSettings = performanceSettingsQuery.data ?? null;

  // Tenant-wide (not per-date, unlike performanceSettingsQuery) — Calculation
  // Policy Version has no effectiveTo/date-scoping concept today, see
  // NORMAX_PERFORMANCE_DAILY_ENTRY_FINALIZATION_UX_FIX_REPORT.md §3. `data:
  // null` (not an error) is the explicit "not configured for this tenant"
  // signal, same contract as companyPerformanceSettingsApi.current().
  const calcPolicyQuery = useQuery({
    queryKey: ["calculation-policy", "current"],
    queryFn: () => calculationPolicyApi.current(),
  });
  const calcPolicyMissing = !calcPolicyQuery.isLoading && !calcPolicyQuery.data;

  // Finish-Day gating fix (NORMAX_PERFORMANCE_NEUTRAL_UNNORMED_FORENSIC_REPORT.md
  // §10): `calcPolicyMissing` alone is TENANT-WIDE — it says nothing about
  // whether THIS day actually contains a NORMED_WORK line, the only entry
  // type Method C (and therefore the policy) ever applies to. The backend's
  // own `updateStatus` precondition (daily-entry.service.ts) is exactly
  // `entry.lines.some(l => l.entryType === EntryType.NORMED_WORK)` — a plain
  // presence check, nothing more elaborate — so this mirrors that same
  // simple check against the current local rows, never re-deriving Method C
  // or any other calculation locally. The backend remains authoritative: this
  // only controls whether the button is disabled/the warning is shown, never
  // whether a Finish Day request actually succeeds.
  const hasNormedWork = rows.some(isNormedRow);
  const finishDayBlockedByPolicy = hasNormedWork && calcPolicyMissing;

  function handleGoToSettings() {
    navigate("/podesavanja#nx-settings-calcpolicy-section");
  }

  // Load server state (or offer local draft recovery) whenever worker/date settles.
  useEffect(() => {
    if (!workerId || !workDate || entryQuery.isLoading) return;

    const snapshot = loadDraftSnapshot(workerId, workDate);
    const serverEntry = entryQuery.data ?? null;

    // A stale error from a PREVIOUS worker/date's failed save/finalize must
    // never carry over to a different worker/date's context (Finalization
    // UX Fix §7) — cleared here, not just at the start of a new save attempt.
    setSaveError(null);

    if (snapshot && snapshot.rows.length > 0) {
      setRecoverableDraft({ savedAt: snapshot.savedAt, rows: snapshot.rows });
    } else {
      setRecoverableDraft(null);
    }

    if (serverEntry) {
      setEntryId(serverEntry.id);
      setEntryStatus(serverEntry.status);
      if (!snapshot) setRows(serverEntry.lines.map(lineToRow));
    } else {
      setEntryId(null);
      setEntryStatus("draft");
      if (!snapshot) setRows([]);
    }
    setHasUnsavedChanges(false);
  }, [workerId, workDate, entryQuery.data, entryQuery.isLoading]);

  useEffect(() => {
    setHasUnsavedChanges(rows.some((r) => r.dirty));
  }, [rows]);

  useDraftAutosave(workerId, workDate, rows, hasUnsavedChanges);

  const totalMinutes = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.durationMinutes) || 0), 0),
    [rows],
  );

  // "Potvrdi unos" / "Ponovo otvori" (2026-09-09 brainstorm) — a CONFIRMED
  // day locks the grid until explicitly reopened; the client-side
  // exact-match check below is a UX convenience only (disables the
  // button, shows a hint) — the backend remains the real authority
  // (`DailyEntryService.updateStatus`, compared as Decimal there).
  // `Math.abs(...) < 1e-9` avoids float-sum precision traps (e.g.
  // 33.33+33.33+33.34), not a "close enough" tolerance for the user.
  const gridReadOnly = isDirector || entryStatus === "confirmed";
  const expectedMinutes = performanceSettings?.standardAvailableMinutes ?? null;
  const isMinutesExactMatch = expectedMinutes !== null && Math.abs(totalMinutes - expectedMinutes) < 1e-9;

  const dayAggregate = useDayAggregatePreview(rows, validCombinationsQuery.data ?? []);
  const dayZone =
    dayAggregate.performanceRatioRaw !== null && performanceSettings
      ? classifyPerformanceZone(Number(dayAggregate.performanceRatioRaw) * 100, performanceSettings)
      : null;
  const ZONE_TONE = { below: "danger", neutral: "neutral", above: "success" } as const;

  const guardedNavigate = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        setPendingNav(() => action);
      } else {
        action();
      }
    },
    [hasUnsavedChanges],
  );

  /**
   * Persists every dirty row's create/update (never delete — deletion is
   * now immediate, handled by `confirmDeleteRow` below, the moment the row
   * is removed, not deferred to this point). Returns the entry id (creating
   * the entry shell first if this is the very first save), or `null` on
   * failure — `saveError` is already set by the time it returns `null`.
   */
  async function persistDirtyRows(): Promise<string | null> {
    if (!workerId) return null;
    setSaveError(null);
    let id = entryId;
    // Declared OUTSIDE the try block and mutated in place as each row
    // succeeds — audit finding #5: a PARTIAL failure (row A saves, row B
    // then fails) must still commit row A's new serverId/dirty:false
    // locally. Without this, a retry would call addLine for row A again,
    // creating a genuine duplicate WorkLine server-side.
    const nextRows = [...rows];
    try {
      if (!id) {
        const created = await dailyEntryApi.create(workerId, workDate);
        id = created.id;
        setEntryId(id);
        setEntryStatus(created.status);
      }

      for (let i = 0; i < nextRows.length; i++) {
        const row = nextRows[i]!;
        if (!row.dirty || !isRowComplete(row)) continue;

        if (!row.serverId) {
          const created = await dailyEntryApi.addLine(id, rowToCreateInput(row));
          nextRows[i] = { ...lineToRow(created) };
        } else {
          const patch: UpdateWorkLineInput = rowToCreateInput(row);
          const updated = await workLinesApi.update(row.serverId, patch);
          nextRows[i] = { ...lineToRow(updated) };
        }
      }

      setRows(nextRows);
      setHasUnsavedChanges(false);
      clearDraftSnapshot(workerId, workDate);
      return id;
    } catch (error) {
      // Commit whatever DID succeed before the row that threw — see the
      // comment above `nextRows`.
      setRows(nextRows);
      setSaveError(mapDailyEntryErrorMessage(error, t, "Nema veze sa serverom. Čuvanje nije uspelo."));
      return null;
    }
  }

  /**
   * "Potvrdi unos" (2026-09-09 brainstorm) — replaces the old two-button
   * Sačuvaj/Završi dan flow with one action: persist every pending row
   * change, then confirm. The backend is the real authority on both
   * preconditions (Calculation Policy, exact minutes-match against the
   * daily fund) — `isMinutesExactMatch` only disables the button as a UX
   * convenience; a genuine mismatch still surfaces here as `saveError`
   * exactly like any other failure.
   *
   * `setSaveError(null)` on success clears any STALE error left over from
   * an earlier, unrelated failed attempt — same reasoning as the original
   * Finalization UX Fix §7 this replaces.
   */
  const handleConfirm = useCallback(async () => {
    setIsSaving(true);
    try {
      const id = await persistDirtyRows();
      if (!id) return; // persistDirtyRows already set saveError
      const updated = await dailyEntryApi.updateStatus(id, "confirmed");
      setEntryStatus(updated.status);
      setSaveError(null);
      showToast({ title: "Unos je potvrđen.", tone: "success" });
      pendingNav?.();
      setPendingNav(null);
    } catch (error) {
      setSaveError(mapDailyEntryErrorMessage(error, t, "Nema veze sa serverom. Potvrda nije uspela."));
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId, workDate, entryId, rows, showToast, t, pendingNav]);

  /**
   * "Ponovo otvori" — the other half of the toggle. No precondition (the
   * user's own instruction: friction belongs to deletion, not to
   * reopening for a correction) — unlocks the grid (`gridReadOnly` above
   * turns false once `entryStatus` is no longer "confirmed") so rows can
   * be edited again, ending with another "Potvrdi unos".
   */
  const handleReopen = useCallback(async () => {
    if (!entryId) return;
    try {
      const updated = await dailyEntryApi.updateStatus(entryId, "draft");
      setEntryStatus(updated.status);
      setSaveError(null);
    } catch (error) {
      setSaveError(mapDailyEntryErrorMessage(error, t, "Nema veze sa serverom. Ponovno otvaranje nije uspelo."));
    }
  }, [entryId, t]);

  /**
   * Deletion is never silent (2026-09-09 brainstorm) — opens the
   * mandatory-reason prompt instead of removing the row immediately. Rows
   * with no `serverId` (never saved) are removed for free by `EntryGrid`
   * itself and never reach this handler — see its `onDeleteRow` contract.
   */
  const handleRequestDeleteRow = useCallback((row: RowState) => {
    setDeleteRowPrompt(row);
    setDeleteRowReason("");
    setDeleteError(null);
  }, []);

  /**
   * Invalidates the cached entry lookup (and the deleted-info lookup) for
   * this worker/date after ANY successful deletion — audit finding #1.
   * Without this, `entryQuery`'s cache still holds the pre-deletion entry;
   * a later remount for the SAME worker/date (react-query's
   * `refetchOnMount`) would briefly show it again before a background
   * refetch corrects it. Manually resetting component state (done at each
   * call site) is not enough on its own — the cache itself must agree.
   */
  function invalidateEntryQueries() {
    void queryClient.invalidateQueries({ queryKey: ["daily-entry", workerId, workDate] });
    void queryClient.invalidateQueries({ queryKey: ["daily-entry-deleted-info", workerId, workDate] });
  }

  const confirmDeleteRow = useCallback(async () => {
    const row = deleteRowPrompt;
    if (!row?.serverId) return;
    setDeleteError(null);
    try {
      await workLinesApi.remove(row.serverId, deleteRowReason);
      // Deleting the LAST remaining persisted line also deletes the whole
      // day server-side (DailyEntryService.deleteLine) — mirror that
      // locally so the page resets to "no entry" rather than showing a
      // stale entryId/status for a row that no longer exists.
      const wasLastPersistedRow = !rows.some((r) => r.serverId && r.serverId !== row.serverId);
      setRows((current) => current.filter((r) => r.localId !== row.localId));
      if (wasLastPersistedRow) {
        setEntryId(null);
        setEntryStatus("draft");
      }
      invalidateEntryQueries();
      setDeleteRowPrompt(null);
      setDeleteRowReason("");
      showToast({ title: "Red obrisan.", tone: "success" });
    } catch (error) {
      // Shown INSIDE the still-open modal (audit finding #2) — a
      // page-level `saveError` banner would be hidden behind the modal's
      // own overlay, making a failure look like nothing happened.
      setDeleteError(mapDailyEntryErrorMessage(error, t, "Nema veze sa serverom. Brisanje nije uspelo."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRowPrompt, deleteRowReason, rows, showToast, t, workerId, workDate]);

  /** "Obriši ceo dan" — explicit whole-day delete, always available (not gated by the minutes-match/Calc Policy preconditions above, which only apply to CONFIRMING). */
  const confirmDeleteEntry = useCallback(async () => {
    if (!entryId) return;
    setDeleteError(null);
    try {
      await dailyEntryApi.deleteEntry(entryId, deleteEntryReason);
      setEntryId(null);
      setEntryStatus("draft");
      setRows([]);
      setHasUnsavedChanges(false);
      if (workerId) clearDraftSnapshot(workerId, workDate);
      invalidateEntryQueries();
      setDeleteEntryModalOpen(false);
      setDeleteEntryReason("");
      showToast({ title: "Dnevni unos je obrisan.", tone: "success" });
    } catch (error) {
      setDeleteError(mapDailyEntryErrorMessage(error, t, "Nema veze sa serverom. Brisanje nije uspelo."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, deleteEntryReason, workerId, workDate, showToast, t]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!gridReadOnly && isMinutesExactMatch) void handleConfirm();
      } else if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        workerFieldRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleConfirm, gridReadOnly, isMinutesExactMatch]);

  const isEmptyCatalog =
    !workersQuery.isLoading && !productsQuery.isLoading && (workersQuery.data?.length ?? 0) === 0;

  return (
    <div className="nx-daily-entry">
      <div className="nx-daily-entry__title-row">
        <h1>{readOnly ? "Dnevni unos (samo za pregled)" : "Dnevni unos"}</h1>
        {entryId ? <DraftStatusBadge status={entryStatus} /> : null}
        {entryId ? (
          // "secondary" (visible border/background at rest, not just on
          // hover) so this reads as an obvious button next to the status
          // badge, not plain text — "ghost" was the reported problem
          // (Daily Entry Audit History UI Polish §3).
          <Button variant="secondary" size="sm" onClick={() => setHistoryModalOpen(true)}>
            <History size={16} />
            {t("history.action")}
          </Button>
        ) : null}
      </div>

      {isEmptyCatalog ? (
        <p className="nx-daily-entry__empty-catalog">Nema unetih radnika u sistemu.</p>
      ) : (
        <>
          <EntryHeader
            workDate={workDate}
            onWorkDateChange={(d) => guardedNavigate(() => setWorkDate(d))}
            workerId={workerId}
            onWorkerChange={(id) => guardedNavigate(() => setWorkerId(id))}
            workers={workersQuery.data ?? []}
            disabled={isSaving}
          />

          {recoverableDraft ? (
            <DraftRecoveryBanner
              savedAt={recoverableDraft.savedAt}
              onRestore={() => {
                setRows(recoverableDraft.rows);
                setRecoverableDraft(null);
              }}
              onDiscard={() => {
                if (workerId) clearDraftSnapshot(workerId, workDate);
                setRecoverableDraft(null);
              }}
            />
          ) : null}

          {workerId && !entryId && !entryQuery.isLoading && deletedInfoQuery.data ? (
            <p className="nx-daily-entry__deleted-info" role="status">
              {t("deletedInfo.message", {
                date: new Date(deletedInfoQuery.data.deletedAt).toLocaleDateString(intlLocale),
                byClause: deletedInfoQuery.data.deletedByEmail
                  ? t("deletedInfo.byClause", { email: deletedInfoQuery.data.deletedByEmail })
                  : "",
                reasonClause: deletedInfoQuery.data.reason
                  ? t("deletedInfo.reasonClause", { reason: deletedInfoQuery.data.reason })
                  : "",
              })}
            </p>
          ) : null}

          {!workerId ? (
            <p className="nx-daily-entry__hint">Izaberite radnika da biste videli ili uneli dnevni unos.</p>
          ) : entryQuery.isLoading ? (
            <div className="nx-daily-entry__skeleton" aria-hidden="true">
              {[1, 2, 3].map((i) => (
                <div key={i} className="nx-daily-entry__skeleton-row" />
              ))}
            </div>
          ) : (
            <>
              <EntryGrid
                rows={rows}
                onRowsChange={setRows}
                onDeleteRow={handleRequestDeleteRow}
                products={productsQuery.data ?? []}
                operations={operationsQuery.data ?? []}
                neutralActivities={neutralActivitiesQuery.data ?? []}
                units={unitsQuery.data ?? []}
                validCombinations={validCombinationsQuery.data ?? []}
                readOnly={gridReadOnly}
                thresholds={performanceSettings ?? undefined}
              />

              <div className="nx-daily-entry__footer">
                <div className="nx-daily-entry__summary">
                  <MinutesSumBadge
                    totalMinutes={totalMinutes}
                    expectedMinutes={performanceSettings?.standardAvailableMinutes ?? 0}
                  />
                  {rows.length > 0 ? (
                    dayAggregate.hasNormedWork ? (
                      <span className="nx-daily-entry__day-ratio">
                        Ostvarenje norme (dan): <strong>{dayAggregate.performanceRatioLabel}</strong>{" "}
                        {dayZone ? <Badge tone={ZONE_TONE[dayZone.zone]}>{dayZone.label}</Badge> : null}{" "}
                        ({dayAggregate.deviationLabel}) na {dayAggregate.normedMinutesLabel}
                      </span>
                    ) : (
                      <span className="nx-daily-entry__day-ratio nx-daily-entry__day-ratio--muted">
                        Nema normiranog rada u periodu
                      </span>
                    )
                  ) : null}
                </div>
                {!isDirector ? (
                  <div className="nx-daily-entry__footer-actions">
                    {entryStatus === "confirmed" ? (
                      // "Ponovo otvori" — no precondition, unlocks the grid
                      // (gridReadOnly turns false once status is no longer
                      // "confirmed"). Friction belongs to deletion, not to
                      // reopening for a correction (2026-09-09 brainstorm).
                      <Button variant="secondary" onClick={() => void handleReopen()}>
                        <RotateCcw size={16} />
                        {t("actions.reopen")}
                      </Button>
                    ) : (
                      // "Potvrdi unos" persists every dirty row AND confirms
                      // in one action — replaces the old Sačuvaj/Završi dan
                      // pair. Disabled unless BOTH preconditions the backend
                      // itself enforces are already met client-side: the
                      // Calculation Policy (only when the day has NORMED_WORK)
                      // and an EXACT match between entered minutes and the
                      // active daily fund — a genuine failure still surfaces
                      // inline via `saveError`, exactly like any other save
                      // failure, since the backend remains the real authority.
                      <Button
                        variant="primary"
                        onClick={() => void handleConfirm()}
                        loading={isSaving}
                        disabled={finishDayBlockedByPolicy || !isMinutesExactMatch}
                      >
                        <Check size={16} />
                        {t("actions.confirm")}
                      </Button>
                    )}
                    {entryId ? (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteEntryModalOpen(true);
                        }}
                      >
                        {t("actions.deleteEntry")}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {!isDirector && entryStatus !== "confirmed" && finishDayBlockedByPolicy ? (
                <div className="nx-daily-entry__policy-missing" role="status">
                  <p className="nx-daily-entry__policy-missing-title">{t("calculationPolicy.missingTitle")}</p>
                  <p className="nx-field__hint">{t("calculationPolicy.missingBody")}</p>
                  <Button type="button" variant="secondary" size="sm" onClick={handleGoToSettings}>
                    <ArrowRight size={16} />
                    {t("calculationPolicy.goToSettings")}
                  </Button>
                </div>
              ) : null}

              {saveError ? (
                <div className="nx-daily-entry__error" role="alert">
                  <span>⚠ {saveError}</span>
                  <Button size="sm" variant="secondary" onClick={() => void handleConfirm()}>
                    Pokušaj ponovo
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      <Modal
        open={!!pendingNav}
        onOpenChange={(open) => !open && setPendingNav(null)}
        title="Nesačuvane izmene"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingNav(null)}>
              Otkaži
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                pendingNav?.();
                setPendingNav(null);
              }}
            >
              Nastavi bez čuvanja
            </Button>
          </>
        }
      >
        <p>Imate nesačuvane izmene na ovom danu. Ako nastavite, izmene će biti izgubljene.</p>
      </Modal>

      <Modal
        open={!!deleteRowPrompt}
        onOpenChange={(open) => !open && setDeleteRowPrompt(null)}
        title={t("delete.row.modalTitle")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteRowPrompt(null)}>
              {t("delete.cancel")}
            </Button>
            <Button
              variant="danger"
              disabled={deleteRowReason.trim() === ""}
              onClick={() => void confirmDeleteRow()}
            >
              {t("delete.row.confirm")}
            </Button>
          </>
        }
      >
        <p>{t("delete.row.body")}</p>
        {deleteError ? (
          <p className="nx-daily-entry__error" role="alert">
            ⚠ {deleteError}
          </p>
        ) : null}
        <Input
          label={t("delete.reasonLabel")}
          required
          value={deleteRowReason}
          onChange={(e) => setDeleteRowReason(e.target.value)}
        />
      </Modal>

      <Modal
        open={deleteEntryModalOpen}
        onOpenChange={(open) => {
          setDeleteEntryModalOpen(open);
          if (!open) setDeleteEntryReason("");
        }}
        title={t("delete.entry.modalTitle")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteEntryModalOpen(false)}>
              {t("delete.cancel")}
            </Button>
            <Button
              variant="danger"
              disabled={deleteEntryReason.trim() === ""}
              onClick={() => void confirmDeleteEntry()}
            >
              {t("delete.entry.confirm")}
            </Button>
          </>
        }
      >
        <p>{t("delete.entry.body")}</p>
        {deleteError ? (
          <p className="nx-daily-entry__error" role="alert">
            ⚠ {deleteError}
          </p>
        ) : null}
        <Input
          label={t("delete.reasonLabel")}
          required
          value={deleteEntryReason}
          onChange={(e) => setDeleteEntryReason(e.target.value)}
        />
      </Modal>

      <DailyEntryHistoryModal
        open={historyModalOpen}
        onOpenChange={setHistoryModalOpen}
        dailyEntryId={entryId}
      />
    </div>
  );
}
